import {
  buildPartnerCapabilityReverseIndex
} from "@apf/framework-config/capability-mappings";
import type {
  FrameworkSnapshot,
  ResolvedPartnerCapabilityMapping,
  ResolvedPartnerCapabilityMappingNode
} from "@apf/framework-config";

export type CapabilityMapView = "partner" | "groundworks";
export type CapabilityTargetKind = "domain-workblock" | "business-module" | "shared-capability";
export type CapabilityCoverageStatus = "reference" | "pattern" | "roadmap" | "unmapped";
export type CapabilityModuleImplementationStatus = "reference" | "pattern" | "roadmap";

export interface CapabilityCoverageModule {
  id: string;
  labelKey: string;
  domainWorkblockId: string;
  implementationStatus: CapabilityModuleImplementationStatus;
}

export interface CapabilityCoverage {
  status: CapabilityCoverageStatus;
  modules: CapabilityCoverageModule[];
  isMixed: boolean;
  hasModuleTarget: boolean;
}

export interface CapabilityMappingLinkItem {
  id: string;
  label: string;
  context?: string;
  fit: ResolvedPartnerCapabilityMappingNode["groundworks"]["fit"];
  fitLabel: string;
  to: string;
}

export function capabilityMapHref({
  view,
  mapId,
  anchor
}: {
  view: CapabilityMapView;
  mapId?: string;
  anchor?: string;
}): string {
  const parameters = new URLSearchParams({ view });
  if (mapId) parameters.set("map", mapId);
  return `/capability-map?${parameters.toString()}${anchor ? `#${anchor}` : ""}`;
}

export function capabilityMapAnchor(kind: CapabilityTargetKind | "area" | "capability", id: string): string {
  if (kind === "domain-workblock") return `workblock-${id}`;
  if (kind === "business-module") return `module-${id}`;
  if (kind === "shared-capability") return `shared-capability-${id}`;
  return `${kind}-${id}`;
}

export function resolvePartnerCapabilityMapping(
  snapshot: FrameworkSnapshot,
  requestedMapId?: string | null
): ResolvedPartnerCapabilityMapping | undefined {
  const id = requestedMapId ?? snapshot.defaultPartnerCapabilityMappingId;
  return snapshot.partnerCapabilityMappings?.find((mapping) => mapping.id === id);
}

export function partnerCapabilityAreas(mapping: ResolvedPartnerCapabilityMapping) {
  return mapping.nodes
    .filter((node) => node.kind !== "capability")
    .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id));
}

export function partnerCapabilitiesInArea(mapping: ResolvedPartnerCapabilityMapping, areaId: string) {
  return mapping.nodes
    .filter((node) => node.kind === "capability" && node.parentId === areaId)
    .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id));
}

/**
 * Derive overview coverage only from mapped business-module implementation
 * status. A correspondence, domain target or shared capability never becomes
 * implementation proof merely because it appears in the crosswalk.
 */
export function derivePartnerCapabilityCoverage(
  snapshot: FrameworkSnapshot,
  node: ResolvedPartnerCapabilityMappingNode
): CapabilityCoverage {
  const moduleById = new Map(
    snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module])
  );
  const modules = node.groundworks.businessModuleIds.flatMap((id) => {
    const module = moduleById.get(id);
    return module ? [{
      id: module.id,
      labelKey: module.labelKey,
      domainWorkblockId: module.domainWorkblockId,
      implementationStatus: module.implementationStatus
    }] : [];
  });
  const statuses = new Set(modules.map(({ implementationStatus }) => implementationStatus));
  const hasModuleTarget = node.groundworks.businessModuleIds.length > 0;
  const status: CapabilityCoverageStatus = statuses.has("reference")
    ? "reference"
    : statuses.has("pattern")
      ? "pattern"
      : hasModuleTarget
        ? "roadmap"
        : "unmapped";

  return {
    status,
    modules,
    isMixed: statuses.size > 1,
    hasModuleTarget
  };
}

function fitLabel(copy: (key: string, fallback?: string) => string, fit: CapabilityMappingLinkItem["fit"]): string {
  return copy(`partner-capability.fit.${fit}`);
}

/**
 * Resolve every partner capability that points to one GroundWorks target.
 * The authored map remains one-way; this selector is the only inverse used by
 * detail tags, browser search and the GroundWorks-first capability view.
 */
export function partnerCapabilityLinksForTarget(
  snapshot: FrameworkSnapshot,
  copy: (key: string, fallback?: string) => string,
  kind: CapabilityTargetKind,
  targetId: string
): CapabilityMappingLinkItem[] {
  return (snapshot.partnerCapabilityMappings ?? []).flatMap((mapping) => {
    const index = buildPartnerCapabilityReverseIndex(mapping);
    const nodes = kind === "domain-workblock"
      ? index.byDomainWorkblockId[targetId]
      : kind === "business-module"
        ? index.byBusinessModuleId[targetId]
        : index.bySharedCapabilityId[targetId];
    const areaById = new Map(partnerCapabilityAreas(mapping).map((area) => [area.id, area]));
    return (nodes ?? []).map((node) => {
      const area = areaById.get(node.parentId ?? "");
      return {
        id: `${mapping.id}:${node.id}`,
        label: copy(node.labelKey),
        context: area ? copy(area.labelKey) : undefined,
        fit: node.groundworks.fit,
        fitLabel: fitLabel(copy, node.groundworks.fit),
        to: capabilityMapHref({
          view: "partner",
          mapId: mapping.id,
          anchor: capabilityMapAnchor("capability", node.id)
        })
      };
    });
  });
}

export function groundworksLinksForPartnerCapability(
  snapshot: FrameworkSnapshot,
  mapping: ResolvedPartnerCapabilityMapping,
  node: ResolvedPartnerCapabilityMappingNode,
  copy: (key: string, fallback?: string) => string
): Array<{ id: string; label: string; items: CapabilityMappingLinkItem[] }> {
  const domainById = new Map(snapshot.content.domainWorkblocks.map((domain) => [domain.id, domain]));
  const moduleById = new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module]));
  const sharedById = new Map(snapshot.content.capabilities.map((capability) => [capability.id, capability]));
  const label = fitLabel(copy, node.groundworks.fit);
  const item = (kind: CapabilityTargetKind, id: string, targetLabel: string): CapabilityMappingLinkItem => ({
    id: `${kind}:${id}`,
    label: targetLabel,
    fit: node.groundworks.fit,
    fitLabel: label,
    to: capabilityMapHref({
      view: "groundworks",
      mapId: mapping.id,
      anchor: capabilityMapAnchor(kind, id)
    })
  });
  const groups = [
    {
      id: "workblocks",
      label: copy("partner-capability.targets.workblocks"),
      items: node.groundworks.domainWorkblockIds.flatMap((id) => {
        const domain = domainById.get(id);
        return domain ? [item("domain-workblock", id, copy(domain.nameKey))] : [];
      })
    },
    {
      id: "modules",
      label: copy("partner-capability.targets.modules"),
      items: node.groundworks.businessModuleIds.flatMap((id) => {
        const module = moduleById.get(id);
        return module ? [item("business-module", id, copy(module.labelKey))] : [];
      })
    },
    {
      id: "shared",
      label: copy("partner-capability.targets.shared"),
      items: node.groundworks.sharedCapabilityIds.flatMap((id) => {
        const capability = sharedById.get(id);
        return capability ? [item("shared-capability", id, copy(capability.labelKey))] : [];
      })
    }
  ];
  return groups.filter(({ items }) => items.length > 0);
}
